import moment from "moment";
import { getFormattedPessoaCnpj, formatCNPJFromParts } from "@/utils";
import { FREIGHT_VALIDITY_STATUS } from "../constants/freightValidityStatus";
import {
  FREIGHT_TABLE_STATUS,
  FREIGHT_TABLE_STATUS_LABEL,
} from "../constants/freightTableStatus";
import {
  isValidityBlank,
  valorSaidaLineVehicles,
  validityVehicles,
} from "../services/freightTableConfigForm";
import { mapCustosExtraToApi, mapTaxasToApi } from "./validityTaxes";
import {
  formatOutrosDecimal,
  formatOutrosMoney,
} from "./freightTableOutrosDecimal";
import {
  FREIGHT_TABLE_TIPO_RECEITA,
  ROUTE_LOCALITY_MUNICIPIO,
  ROUTE_LOCALITY_REGIAO,
  calcIsFaixaPesoPorVeiculo,
  calcUsesVehicle,
  mapTpCalcOptionLabel,
} from "../constants/freightTableOptions";

function relation(raw, ...keys) {
  if (!raw) return null;

  for (let i = 0; i < keys.length; i += 1) {
    const value = raw[keys[i]];
    if (value && typeof value === "object") return value;
  }

  return null;
}


function statusPair(raw) {
  const rel = relation(raw, "Status", "status");
  const rawValor =
    rel && rel.valor !== undefined ? rel.valor : raw && raw.status;
  const valor =
    rawValor === undefined || rawValor === null ? null : Number(rawValor);

  return {
    descr: (rel && rel.descr) || FREIGHT_TABLE_STATUS_LABEL[valor] || "",
    valor,
  };
}

function mapPessoaIdentity(pessoa) {
  const pj = relation(pessoa, "PessoaJuridica", "pessoa_juridica");
  if (pj) {
    return {
      nome: pj.razao_social || "",
      documento: getFormattedPessoaCnpj(pj),
      inscricaoEstadual: pj.inscricao_estadual || "",
    };
  }

  const pf = relation(pessoa, "PessoaFisica", "pessoa_fisica");
  if (pf) {
    return {
      nome: pf.nome || "",
      documento: pf.cpf || "",
      inscricaoEstadual: "",
    };
  }

  const pe = relation(pessoa, "PessoaExterior", "pessoa_exterior");
  if (pe) {
    return {
      nome: pe.nome || "",
      documento: pe.cd || "",
      inscricaoEstadual: "",
    };
  }

  return { nome: "", documento: "", inscricaoEstadual: "" };
}

export function getLegacyCarriers(raw) {
  return (
    relation(raw, "TabelaFreteTransportador", "tabela_frete_transportador") ||
    []
  );
}

/**
 * Linha da listagem no contrato novo — `GET /tabelafrete/listagem`.
 *
 * Ao contrário do `mapFreightTableFromLegacyApi`, não deriva nada da árvore de rotas: as três
 * contagens de vigência vêm prontas do backend. Só a listagem usa este mapper; o Detalhes segue
 * no formato cru.
 */
export function mapFreightTableFromApi(row) {
  const fornecedor = row.fornecedor || {};

  return {
    id: row.tabfreId,
    freightTableId: row.tabfreId,
    isDraft: !!row.isDraft,
    codigo: row.cd,
    descricao: row.descr || "",
    tipo: (row.tpReceitaDespesa && row.tpReceitaDespesa.descr) || "",
    tipoValor:
      row.tpReceitaDespesa && row.tpReceitaDespesa.valor !== undefined
        ? Number(row.tpReceitaDespesa.valor)
        : null,
    tabela: (row.tpTabela && row.tpTabela.descr) || "",
    calculo: (row.tpCalculo && row.tpCalculo.descr) || "",
    calculoValor:
      row.tpCalculo && row.tpCalculo.valor !== undefined
        ? Number(row.tpCalculo.valor)
        : null,
    fornecedorNome: fornecedor.nome || "",
    // O backend manda dígitos crus; a máscara é a mesma das telas legadas.
    fornecedorDocumento: formatCNPJFromParts(fornecedor.documento || ""),
    fornecedoresExtras: row.fornecedoresExtras || 0,
    totalVigencias: row.totalVigencias || 0,
    vigenciasPendentes: row.vigenciasPendentes || 0,
    vigenciasCancelaveis: row.vigenciasCancelaveis || 0,
    status: (row.status && row.status.descr) || "",
    statusValor:
      row.status && row.status.valor !== undefined
        ? Number(row.status.valor)
        : null,
  };
}

export function mapFreightTableFromLegacyApi(raw) {
  const carriers = getLegacyCarriers(raw);
  const vigencias = getLegacyRoutes(raw).reduce(
    (all, route) => all.concat(getLegacyValidities(route)),
    [],
  );
  const vigenciasPendentes = vigencias.filter(
    (vigencia) =>
      statusPair(vigencia).valor === FREIGHT_VALIDITY_STATUS.PENDENTE,
  ).length;
  const vigenciasCancelaveis = vigencias.filter(
    (vigencia) =>
      statusPair(vigencia).valor !== FREIGHT_VALIDITY_STATUS.CANCELADA,
  ).length;
  const supplier = mapPessoaIdentity(relation(carriers[0], "Pessoa", "pessoa"));
  // Cliente/tomador: só a tabela receita tem, e só os vínculos ativos — o store inativa os
  // antigos em vez de apagar.
  const clientes = (
    relation(raw, "TabelaFretePessoa", "tabela_frete_pessoa") || []
  )
    .filter((vinculo) => statusPair(vinculo).valor === FREIGHT_TABLE_STATUS.ATIVA)
    .map((vinculo) => ({
      id: vinculo.tafrvipes_id,
      municipio: "",
      ...mapPessoaIdentity(relation(vinculo, "Pessoa", "pessoa")),
    }));
  const tipo = relation(raw, "TpReceitaDespesa", "tp_receita_despesa");
  const tabela = relation(raw, "TpTabela", "tp_tabela");
  const calculo = relation(raw, "TpCalculo", "tp_calculo");
  const status = statusPair(raw);

  return {
    id: raw.tabfre_id,
    freightTableId: raw.tabfre_id,
    isDraft: Number(raw.is_draft) === 1,
    codigo: raw.cd,
    descricao: raw.descr || "",
    tipo: tipo ? tipo.descr : "",
    tipoValor: tipo ? Number(tipo.valor) : null,
    tabela: tabela ? tabela.descr : "",
    calculo: calculo ? calculo.descr : "",
    calculoValor: calculo ? Number(calculo.valor) : null,
    fornecedorNome: supplier.nome,
    fornecedorDocumento: supplier.documento,
    fornecedoresExtras: carriers.length > 1 ? carriers.length - 1 : 0,
    clientes,
    totalVigencias: vigencias.length,
    vigenciasPendentes,
    vigenciasCancelaveis,
    status: status.descr,
    statusValor: status.valor,
  };
}

export function mapFreightTableDetailsFromLegacyApi(raw) {
  const modais = relation(raw, "TabelaFreteModal", "tabela_frete_modal") || [];
  const modal = relation(modais[0], "BModal", "b_modal");
  const composicao = relation(raw, "TpComposicao", "tp_composicao");

  return {
    ...mapFreightTableFromLegacyApi(raw),
    observacao: raw.obs || "",
    modal: modal ? modal.descr : "",
    composicao: composicao ? composicao.descr : "",
  };
}

export function mapCarrierFromLegacyApi(raw) {
  const identity = mapPessoaIdentity(relation(raw, "Pessoa", "pessoa"));
  const status = statusPair(raw);

  return {
    id: raw.tabfretransp_id,
    carrierId: raw.tabfretransp_id,
    nome: identity.nome,
    documento: identity.documento,
    municipio: "",
    inscricaoEstadual: identity.inscricaoEstadual,
    status: status.descr,
    statusValor: status.valor,
  };
}

export function mapCarrierToSupplier(raw) {
  const pessoa = relation(raw, "Pessoa", "pessoa");
  const pessoaId =
    pessoa && pessoa.pessoa_id != null ? pessoa.pessoa_id : raw && raw.pessoa_id;
  if (pessoaId === undefined || pessoaId === null) return null;

  const identity = mapPessoaIdentity(pessoa);

  return {
    pessoa_id: Number(pessoaId),
    nome_fantasia: identity.nome,
    cnpj: identity.documento,
  };
}

export function mapActiveCarriersToSuppliers(raw) {
  return getLegacyCarriers(raw)
    .filter(
      (carrier) => statusPair(carrier).valor === FREIGHT_TABLE_STATUS.ATIVA,
    )
    .map(mapCarrierToSupplier)
    .filter(Boolean);
}

// Ativas e inativas. A tela de inclusão usa isso para não oferecer de novo
// quem já está na tabela — inclusive o inativo, que o PATCH reativaria.
export function mapLinkedCarrierIds(raw) {
  return getLegacyCarriers(raw)
    .map(mapCarrierToSupplier)
    .filter(Boolean)
    .map((supplier) => supplier.pessoa_id);
}

const MONTHS_PT = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const HISTORY_FIELDS = [
  { key: "cd", label: "Código" },
  { key: "descr", label: "Nome" },
  { key: "obs", label: "Observação" },
  { key: "tp_tabela", label: "Tipo de tabela", options: "tpTabela" },
  { key: "tp_calc", label: "Tipo de cálculo", options: "tpCalc" },
  { key: "tp_receita_despesa", label: "Tipo", options: "tpReceitaDespesa" },
  { key: "tp_composicao", label: "Composição", options: "tpComposicao" },
  { key: "status", label: "Status", format: historyStatusLabel },
  { key: "is_draft", label: "Situação", format: historyDraftLabel },
];

const HISTORY_EMPTY = "—";

function historyInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "--";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function historyStatusLabel(value) {
  return Number(value) === FREIGHT_TABLE_STATUS.ATIVA ? "Ativa" : "Inativa";
}

function historyDraftLabel(value) {
  return Number(value) === 1 ? "Rascunho" : "Finalizada";
}

function historyOptionLabel(options, listName, value) {
  const list = (options && options[listName]) || [];
  const match = list.filter((item) => item.value === Number(value))[0];
  return match ? match.label : String(value);
}

function historyValueLabel(field, values, options) {
  const value = values[field.key];
  if (value === undefined || value === null || value === "") {
    return HISTORY_EMPTY;
  }
  if (field.format) return field.format(value);
  if (field.options) return historyOptionLabel(options, field.options, value);
  return String(value);
}

function historyLinkDescription(values, names, labels) {
  const ids = (values && values.pessoa_id) || [];
  const list = Array.isArray(ids) ? ids : [];
  const resolved = list
    .map((id) => (names || {})[id])
    .filter(Boolean)
    .map((nome) => `«${nome}»`);
  const todosResolvidos = resolved.length === list.length;

  if (todosResolvidos && resolved.length === 1) {
    return `${labels.singular} ${resolved[0]} ${labels.actionSingular}`;
  }
  if (todosResolvidos && resolved.length > 1) {
    return `${labels.plural} ${labels.actionPlural}: ${resolved.join(", ")}`;
  }

  if (list.length === 1) {
    return `1 ${labels.singular.toLowerCase()} ${labels.actionSingular}`;
  }
  if (list.length > 1) {
    return `${list.length} ${labels.plural.toLowerCase()} ${labels.actionPlural}`;
  }

  return `${labels.singular} ${labels.actionSingular}`;
}

function historyRouteParts(route) {
  const numero =
    route.num_rota === undefined || route.num_rota === null
      ? "Rota"
      : `Rota ${String(route.num_rota).padStart(2, "0")}`;
  const origem = route.origem || "";
  const destino = route.roteiro || route.destino || "";
  const trecho =
    origem && destino ? `${origem} → ${destino}` : origem || destino;
  const inativa =
    Number(route.status) === FREIGHT_TABLE_STATUS.INATIVA ? " (inativa)" : "";
  const descricao = `${route.descr ? ` «${route.descr}»` : ""}${inativa}`;

  return { numero, trecho, descricao };
}

function historyRouteLabel(route) {
  const { numero, trecho, descricao } = historyRouteParts(route);
  return trecho ? `${numero}: ${trecho}${descricao}` : `${numero}${descricao}`;
}

function historyRoutesDescription(values, singular, plural) {
  const rotas = (values && values.rota) || [];
  if (!Array.isArray(rotas) || !rotas.length) return `Rota ${singular}`;

  if (rotas.length === 1) {
    const { numero, trecho, descricao } = historyRouteParts(rotas[0]);
    return trecho
      ? `${numero} ${singular}: ${trecho}${descricao}`
      : `${numero} ${singular}${descricao}`;
  }

  return `${rotas.length} rotas ${plural}: ${rotas
    .map(historyRouteLabel)
    .join("; ")}`;
}

function historyVehicleLabel(options, partiprodId) {
  const veiculos = (options && options.veiculos) || [];
  const match = veiculos.filter(
    (option) => Number(option.value) === Number(partiprodId),
  )[0];

  return match ? match.label : `Veículo ${partiprodId}`;
}

const HISTORY_CALC_PERCENTUAL_NF = 7;

function historyPercent(value) {
  return `${formatDecimal(value)}%`;
}

function historyValueDetails(vigencia, options, context) {
  if (Array.isArray(vigencia.veiculos) && vigencia.veiculos.length) {
    return vigencia.veiculos.map(
      (item) =>
        `${historyVehicleLabel(options, item.partiprod_id)} ${formatMoney(item.vlr)}`,
    );
  }

  if (Array.isArray(vigencia.calculo) && vigencia.calculo.length) {
    const total = vigencia.calculo.length;
    return [total === 1 ? "1 faixa" : `${total} faixas`];
  }

  if (vigencia.vlr !== undefined && vigencia.vlr !== null && vigencia.vlr !== "") {
    const percentual =
      context && Number(context.tpCalc) === HISTORY_CALC_PERCENTUAL_NF;
    return [percentual ? historyPercent(vigencia.vlr) : formatMoney(vigencia.vlr)];
  }

  if (vigencia.comissao) {
    const comissao = vigencia.comissao;
    const details = [];
    if (Number(comissao.vlr_taxa) > 0) {
      details.push(`comissão de ${formatMoney(comissao.vlr_taxa)}`);
    }
    if (Number(comissao.taxa_comissao) > 0) {
      details.push(`comissão de ${historyPercent(comissao.taxa_comissao)}`);
    }
    if (Number(comissao.vlr_minimo) > 0) {
      details.push(`mínimo de ${formatMoney(comissao.vlr_minimo)}`);
    }
    return details;
  }

  return [];
}

function historyValidityAddedDescription(oldV, newV, options, context) {
  const rota = ((newV && newV.rota) || [])[0];
  const vigencia = (newV && newV.vigencia) || {};
  const trecho = rota ? historyRouteParts(rota).trecho : "";
  const inicio = formatIsoDate(vigencia.dt_vigencia_ini);
  const fim = vigencia.dt_vigencia_fim
    ? formatIsoDate(vigencia.dt_vigencia_fim)
    : "em aberto";
  const veiculos = historyValueDetails(vigencia, options, context);
  const fechadas = (oldV && oldV.vigencia_fechada) || [];

  const parts = [trecho ? `Vigência incluída em ${trecho}` : "Vigência incluída"];
  if (inicio) parts[0] += `: ${inicio} – ${fim}`;
  if (veiculos.length) parts[0] += ` (${veiculos.join("; ")})`;
  if (Array.isArray(fechadas) && fechadas.length) {
    parts.push(
      fechadas.length === 1
        ? "Vigência anterior encerrada"
        : `${fechadas.length} vigências anteriores encerradas`,
    );
  }
  const rotaAntes = ((oldV && oldV.rota) || [])[0];
  if (
    rotaAntes &&
    rota &&
    Number(rotaAntes.status) !== Number(rota.status) &&
    Number(rota.status) === FREIGHT_TABLE_STATUS.INATIVA
  ) {
    parts.push("Rota inativada");
  }

  return parts.join(". ");
}

function historyDiff(oldV, newV, options) {
  return HISTORY_FIELDS.filter((field) => {
    if (!(field.key in oldV) && !(field.key in newV)) return false;
    const before = oldV[field.key];
    const after = newV[field.key];
    return (
      String(before === undefined || before === null ? "" : before) !==
      String(after === undefined || after === null ? "" : after)
    );
  }).map((field) => {
    const before = historyValueLabel(field, oldV, options);
    const after = historyValueLabel(field, newV, options);
    return `${field.label}: ${before} → ${after}`;
  });
}

const CARRIER_LINKED_LABELS = {
  singular: "Transportadora",
  plural: "Transportadoras",
  actionSingular: "vinculada",
  actionPlural: "vinculadas",
};

const CARRIER_UNLINKED_LABELS = {
  singular: "Transportadora",
  plural: "Transportadoras",
  actionSingular: "desvinculada",
  actionPlural: "desvinculadas",
};

export function buildHistoryDescription(raw, options, names, context) {
  const oldV = raw.oldValues || {};
  const newV = raw.newValues || {};

  switch (raw.event) {
    case "created":
      return "Tabela de frete criada";
    case "updated": {
      const changes = historyDiff(oldV, newV, options);
      return changes.length ? changes.join("; ") : "Detalhes atualizados";
    }
    case "finalized":
      return "Tabela finalizada";
    case "deleted":
      return "Tabela excluída";
    case "status_changed":
      return `Status alterado para ${historyStatusLabel(newV.status)}`;
    case "route_added":
      return historyRoutesDescription(newV, "adicionada", "adicionadas");
    case "route_updated":
      return historyRoutesDescription(newV, "atualizada", "atualizadas");
    case "vigencia_added":
      return historyValidityAddedDescription(oldV, newV, options, context);
    case "pessoa_added":
    case "transportador_added":
      return historyLinkDescription(newV, names, CARRIER_LINKED_LABELS);
    case "pessoa_removed":
    case "transportador_removed":
      return historyLinkDescription(oldV, names, CARRIER_UNLINKED_LABELS);
    default:
      return "Alteração registrada";
  }
}

export function mapHistoryEntryFromApi(raw, options, names, context) {
  const date = raw.changedAt
    ? moment(raw.changedAt, "YYYY-MM-DD HH:mm:ss")
    : null;
  const name =
    raw.changedBy && raw.changedBy.name ? raw.changedBy.name : "Sistema";

  return {
    id: raw.tabelaFreteAuditId,
    event: raw.event,
    description: buildHistoryDescription(raw, options, names, context),
    userName: name,
    initials: historyInitials(name),
    dateKey: date ? date.format("YYYY-MM-DD") : "",
    dayLabel: date ? `${date.date()} de ${MONTHS_PT[date.month()]}` : "",
    yearLabel: date ? String(date.year()) : "",
    timestamp: date ? date.format("DD/MM/YY, HH:mm") : "",
  };
}

export function mapHistoryFromApi(rawList, options, names, context) {
  return (rawList || []).map((raw) =>
    mapHistoryEntryFromApi(raw, options, names, context),
  );
}

export function mapCarrierNamesFromRaw(raw) {
  const names = {};

  getLegacyCarriers(raw).forEach((carrier) => {
    const supplier = mapCarrierToSupplier(carrier);
    if (supplier && supplier.nome_fantasia) {
      names[supplier.pessoa_id] = supplier.nome_fantasia;
    }
  });

  return names;
}

export function mapCarriersToApi(tabfreId, suppliers) {
  return {
    tabfre_id: Number(tabfreId),
    transportador: (suppliers || []).map((supplier) => ({
      pessoa_id: supplier.pessoa_id,
    })),
  };
}

function formatIsoDate(value) {
  if (!value) return "";
  const parts = String(value).split(" ")[0].split("-");
  if (parts.length !== 3) return String(value);
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

export function getLegacyRoutes(raw) {
  return relation(raw, "TabelaFreteRota", "tabela_frete_rota") || [];
}

export function getLegacyCargos(rawRoute) {
  return (
    relation(rawRoute, "TabelaFreteRotaCarga", "tabela_frete_rota_carga") || []
  );
}

export function mapCargoFromLegacyApi(raw, index) {
  const status = statusPair(raw);
  const tipoCarga = relation(raw, "ParamTipoCarga", "param_tipo_carga");
  const tipo = relation(raw, "TpCarga", "tp_carga");

  return {
    id: raw.tabfrerotcarga_id || index,
    tipoCarga: tipoCarga ? tipoCarga.descr : "",
    tipo: tipo ? tipo.descr : "",
    status: status.descr,
    statusValor: status.valor,
  };
}

function booleanLabel(rel) {
  if (!rel) return "";
  if (rel.descr) return rel.descr;
  return Number(rel.valor) === 1 ? "Sim" : "Não";
}

export function getLegacyTransports(rawRoute) {
  return (
    relation(
      rawRoute,
      "TabelaFreteRotaMeioTransp",
      "tabela_frete_rota_meio_transp",
    ) || []
  );
}

export function mapTransportFromLegacyApi(raw, index) {
  const status = statusPair(raw);
  const rodado = relation(raw, "ParamTipoRodado", "param_tipo_rodado");
  const carroceria = relation(
    raw,
    "ParamTipoCarroceria",
    "param_tipo_carroceria",
  );

  // vlr_viagem é o valor de saída por veículo do tp_calc = 8 (TMS-7167). Sempre
  // veio no grafo; ficou sem leitura até 2026-09-04.
  const valorSaida = raw.vlr_viagem;

  return {
    id: raw.tabfrerotmeiotransp_id || index,
    rodado: rodado ? rodado.descr : "",
    carroceria: carroceria ? carroceria.descr : "",
    cargaPerigosa: booleanLabel(
      relation(raw, "CargaPerigosa", "carga_perigosa"),
    ),
    refrigerado: booleanLabel(relation(raw, "Refrigerado", "refrigerado")),
    valorSaida: formatMoney(valorSaida),
    temValorSaida:
      valorSaida !== null && valorSaida !== undefined && valorSaida !== "",
    status: status.descr,
    statusValor: status.valor,
  };
}

export function getLegacyValidities(rawRoute) {
  return (
    relation(rawRoute, "TabelaFreteRotaVig", "tabela_frete_rota_vig") || []
  );
}

function localityLabel(rawRoute, municipioKeys, regiaoKeys) {
  const municipio = relation(rawRoute, ...municipioKeys);
  if (municipio) {
    const uf = relation(municipio, "Uf", "uf");
    const nome = municipio.nome || municipio.descr || "";
    return uf && uf.cd ? `${nome}/${uf.cd}` : nome;
  }

  const regiao = relation(rawRoute, ...regiaoKeys);
  return regiao ? regiao.descr || "" : "";
}

export function mapRouteFromLegacyApi(raw, index) {
  const status = statusPair(raw);
  const origem = localityLabel(
    raw,
    ["MunicipioOrigem", "municipio_origem"],
    ["RegiaoOrigem", "regiao_origem"],
  );
  const destino = localityLabel(
    raw,
    ["MunicipioDestino", "municipio_destino"],
    ["RegiaoDestino", "regiao_destino"],
  );
  const processo = relation(raw, "TpProcesso", "tp_processo");
  const operacao = relation(raw, "TpOperacao", "tp_operacao");
  const cargas =
    relation(raw, "TabelaFreteRotaCarga", "tabela_frete_rota_carga") || [];
  const validities = getLegacyValidities(raw);
  const itinerary = relation(raw, "Itinerary", "itinerary");
  const roteiro = itinerary ? itinerary.descr || itinerary.cd || "" : "";
  const chegada = destino || roteiro;

  const numero = raw.num_rota || index + 1;
  const meta = [];
  if (processo && processo.descr) meta.push(processo.descr);
  if (operacao && operacao.descr) meta.push(operacao.descr);
  if (cargas.length) meta.push(`${cargas.length} carga(s)`);
  meta.push(`${validities.length} vigência(s)`);

  return {
    id: raw.tabfrerot_id,
    routeId: raw.tabfrerot_id,
    nome: `Rota ${String(numero).padStart(2, "0")}`,
    destino: origem && chegada ? `${origem} → ${chegada}` : origem || chegada,
    origem,
    destinoLocal: destino,
    roteiro,
    meta,
    processo: processo ? processo.descr || "" : "",
    operacao: operacao ? operacao.descr || "" : "",
    paradas: [],
    totalParadas: 0,
    status: status.descr,
    statusValor: status.valor,
  };
}

export function mapValidityFromLegacyApi(raw) {
  const status = statusPair(raw);
  const aprovador = relation(raw, "UsuarioAprov", "usuario_aprov");
  const cancelador = relation(raw, "UsuarioCanc", "usuario_canc");
  const revisor = aprovador || cancelador;
  const pessoaFisica = revisor
    ? relation(
        relation(revisor, "Pessoa", "pessoa"),
        "PessoaFisica",
        "pessoa_fisica",
      )
    : null;

  return {
    id: raw.tabfrerotvig_id,
    validityId: raw.tabfrerotvig_id,
    inicio: formatIsoDate(raw.dt_vigencia_ini),
    fim: formatIsoDate(raw.dt_vigencia_fim),
    inicioIso: raw.dt_vigencia_ini || null,
    fimIso: raw.dt_vigencia_fim || null,
    status: status.descr,
    statusValor: status.valor,
    revisadoPor: pessoaFisica ? pessoaFisica.nome || "" : "",
    revisadoEm: formatIsoDate(aprovador ? raw.dt_aprovacao : raw.dt_canc),
  };
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "";

  const number = Number(value);
  if (isNaN(number)) return String(value);

  const parts = number.toFixed(2).split(".");
  const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `R$ ${integer},${parts[1]}`;
}

function formatDecimal(value) {
  if (value === null || value === undefined || value === "") return "";

  const number = Number(value);
  if (isNaN(number)) return String(value);

  return String(number).replace(".", ",");
}

export function mapValidityDetailsFromLegacyApi(raw) {
  const calcs =
    relation(raw, "TabelaFreteRotaVigCalc", "tabela_frete_rota_vig_calc") || [];
  const taxa =
    relation(raw, "TabelaFreteRotaVigTaxa", "tabela_frete_rota_vig_taxa") || {};
  const tipoTde = relation(taxa, "TpTaxaTde", "tp_taxa_tde");
  const tipoTda = relation(taxa, "TpTaxaTda", "tp_taxa_tda");
  const tipoPedagio = relation(taxa, "TpTaxaPedagio", "tp_taxa_pedagio");
  const custosExtras =
    relation(
      raw,
      "TabelaFreteRotaVigCusExt",
      "tabela_frete_rota_vig_cus_ext",
    ) || [];

  return {
    ...mapValidityFromLegacyApi(raw),
    faixas: calcs.map(function (range, index) {
      const status = statusPair(range);
      const veiculo = relation(range, "ParamTipoRodado", "param_tipo_rodado");

      return {
        id: range.tabfrrotvicalc_id || index,
        rangeId: range.tabfrrotvicalc_id || index,
        veiculo: veiculo ? veiculo.descr || "" : "",
        status: status.descr,
        statusValor: status.valor,
        valorInicial: formatDecimal(range.vlr_ini),
        valorFinal: formatDecimal(range.vlr_fim),
        valor: formatMoney(range.vlr),
        valorExcedente: formatMoney(range.vlr_exd),
        fatorConversao: formatDecimal(range.fator_conv),
        fixa: Number(range.fixa) === 1 ? "Sim" : "Não",
        temFixa: Number(range.fixa) === 1,
        excedente: Number(range.excedente) === 1 ? "Sim" : "Não",
        temExcedente: Number(range.excedente) === 1,
      };
    }),
    // Espelha as abas da tela de criação (ADR-0012 §2).
    taxas: {
      tipoTaxaTde: tipoTde ? tipoTde.descr : "",
      taxaTde: formatOutrosMoney(taxa.taxa_tde),
      valorFixoTde: formatOutrosMoney(taxa.vlr_fixo_tde),
      tipoTaxaTda: tipoTda ? tipoTda.descr : "",
      taxaTda: formatOutrosMoney(taxa.taxa_tda),
      valorFixoTda: formatOutrosMoney(taxa.vlr_fixo_tda),
      tipoTaxaPedagio: tipoPedagio ? tipoPedagio.descr : "",
      taxaPedagio: formatOutrosMoney(taxa.taxa_pedagio),
      valorPedagio: formatOutrosMoney(taxa.vlr_pedagio),
      taxaGris: formatOutrosMoney(taxa.taxa_gris),
      minimoGris: formatOutrosMoney(taxa.vlr_minimo_gris),
      adValorem: formatOutrosMoney(taxa.ad_valorem),
      minimoAdValorem: formatOutrosMoney(taxa.vlr_minimo_advalorem),
      valorMinimoNota: formatOutrosMoney(taxa.vlr_min_nota_cte),
      valorTas: formatOutrosMoney(taxa.vlr_tas),
      valorSecCat: formatOutrosMoney(taxa.vlr_sec_cat),
      valorDespacho: formatOutrosMoney(taxa.vlr_despacho),
      valorAgendamento: formatOutrosMoney(taxa.vlr_agendamento),
      valorOutros: formatOutrosMoney(taxa.vlr_outros),
      taxaDevolucao: formatOutrosMoney(taxa.taxa_devolucao),
      taxaReentrega: formatOutrosMoney(taxa.taxa_reentrega),
      taxaColeta: formatOutrosMoney(taxa.taxa_coleta),
      // Coluna vlr_qtde_docs_fiscais — o nome não descreve o campo (ver glossário).
      valorMinimoRota: formatOutrosMoney(taxa.vlr_qtde_docs_fiscais),
      quantidadeNotas: formatDecimal(taxa.qtde_docs_fiscais),
    },
    custosExtras: custosExtras.map(function (custo, index) {
      const trecho = relation(custo, "TpTrecho", "tp_trecho");
      const trechoCusto = relation(custo, "TpTrechoCusto", "tp_trecho_custo");
      const nome = relation(custo, "Custo", "custo");

      return {
        id: custo.tafrvce_id || index,
        trecho: trecho ? trecho.descr || "" : "",
        trechoCusto: trechoCusto ? trechoCusto.descr || "" : "",
        taxa: formatOutrosMoney(custo.vlr_taxa),
        taxaComissao: formatOutrosDecimal(custo.taxa_comissao),
        valorMinimo: formatOutrosMoney(custo.vlr_minimo),
        custo: nome ? nome.descr || "" : "",
      };
    }),
  };
}

function supplierCityLabel(pessoa) {
  const enderecos = relation(pessoa, "PessoaEndereco", "pessoa_endereco") || [];
  const municipio = relation(enderecos[0], "Municipio", "municipio");
  if (!municipio) return "";

  const uf = relation(municipio, "Uf", "uf");
  const sigla = uf ? uf.sigla || uf.cd : "";
  return sigla ? `${municipio.nome} - ${sigla}` : municipio.nome || "";
}

function mapSupplierOption(raw, nome, documento) {
  const pessoa = relation(raw, "Pessoa", "pessoa");
  const pessoaId = raw.pessoa_id || (pessoa && pessoa.pessoa_id);

  return {
    value: Number(pessoaId),
    label: nome || "",
    documento: documento || "",
    municipio: supplierCityLabel(pessoa),
  };
}

export function mapSupplierOptionsFromApi(data) {
  if (!data || typeof data !== "object") return [];

  const juridicas = (data.items || []).map((raw) =>
    mapSupplierOption(
      raw,
      raw.razao_social,
      getFormattedPessoaCnpj({ cnpj: raw.cnpj }),
    ),
  );
  const fisicas = (data.itemsF || []).map((raw) =>
    mapSupplierOption(raw, raw.nome, raw.cpf),
  );
  const exterior = (data.itemsE || []).map((raw) =>
    mapSupplierOption(raw, raw.nome, raw.cuit),
  );

  return juridicas
    .concat(fisicas, exterior)
    .filter((option) => option.value > 0);
}

export function mapDraftFromApi(raw) {
  const data = raw || {};

  return {
    isDraft: !!data.isDraft,
    identification: {
      codigo: data.cd == null ? "" : String(data.cd),
      descricao: data.descr || "",
      observacao: data.obs || "",
    },
    // Radios da Identificação; null quando o GET não os traz, para o form manter o default.
    header: {
      tp_tabela: data.tp_tabela == null ? null : Number(data.tp_tabela),
      tp_receita_despesa:
        data.tp_receita_despesa == null ? null : Number(data.tp_receita_despesa),
      tp_composicao: data.tp_composicao == null ? null : Number(data.tp_composicao),
    },
    suppliers: (data.pessoa || []).map((item) => {
      const identity = mapPessoaIdentity(relation(item, "Pessoa", "pessoa"));
      return {
        pessoa_id: item.pessoa_id,
        nome_fantasia: identity.nome,
        cnpj: identity.documento,
      };
    }),
  };
}

export const IDENTIFICATION_MODAL_DEFAULT = 1;

// `header` são os radios da Identificação (tp_tabela, tp_receita_despesa, tp_composicao):
// vão já no rascunho para ele voltar como foi deixado.
export function mapIdentificationToApi(identification, header = {}) {
  const codigo = String(identification.codigo || "").trim();
  return {
    cd: codigo,
    descr: identification.descricao,
    obs: identification.observacao || "",
    modal: IDENTIFICATION_MODAL_DEFAULT,
    tp_tabela: header.tp_tabela,
    tp_receita_despesa: header.tp_receita_despesa,
    tp_composicao: header.tp_composicao,
  };
}

// Payload de PUT /tabelafrete/store para reeditar Identificação no draft (sem rota).
export function mapIdentificationDraftUpdateToApi(identification, tabfreId) {
  return {
    tabfre_id: tabfreId,
    ...mapIdentificationToApi(identification),
  };
}

export function mapSuppliersToApi(tabfreId, suppliers) {
  return {
    tabfre_id: tabfreId,
    pessoa: suppliers.map((supplier) => ({
      pessoa_id: supplier.pessoa_id,
    })),
  };
}

function compareByLabel(a, b) {
  return String(a.label).localeCompare(String(b.label), "pt-BR", {
    sensitivity: "base",
  });
}

function toNumber(value, fallback) {
  if (value === null || value === undefined || value === "") return fallback;

  const number = Number(value);
  return isNaN(number) ? fallback : number;
}

// Listas de domínio do jsonlistgeral: itens de list_item_valor usam `valor`,
// as tabelas próprias usam a PK (partiprod_id, partipcar_id, tpcarga_id).
const TP_PROCESSO_ORDER = [
  "nao definido",
  "retira",
  "entrega direta",
  "reversa",
  "reversa parceiro",
  "devolucao",
  "devolucao parceiro",
];

function normalizeLabel(label) {
  return String(label === null || label === undefined ? "" : label)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function orderByLabels(options, order) {
  const rank = (option) => {
    const index = order.indexOf(normalizeLabel(option.label));
    return index === -1 ? order.length : index;
  };

  return options.slice().sort((a, b) => rank(a) - rank(b));
}

function domainOptions(rawList, valueKey, options) {
  if (!Array.isArray(rawList)) return [];

  const config = options || {};
  const mapped = rawList.map((item) => ({
    value: Number(item[valueKey]),
    label: item.descr || "",
  }));

  return config.keepApiOrder ? mapped : mapped.sort(compareByLabel);
}

function municipioOptions(rawList) {
  if (!Array.isArray(rawList)) return [];

  return rawList
    .map((item) => {
      const uf = relation(item, "Uf", "uf");
      const sigla = uf ? uf.cd || uf.sigla || "" : "";
      const nome = item.nome || "";

      return {
        value: Number(item.municipio_id),
        label: sigla ? `${nome}/${sigla}` : nome,
      };
    })
    .sort(compareByLabel);
}

export function mapDomainOptionsFromApi(raw) {
  const data = raw || {};

  return {
    tpCalc: domainOptions(data.tp_calc, "valor").map(mapTpCalcOptionLabel),
    tpTabela: domainOptions(data.tp_tabela, "valor"),
    tpReceitaDespesa: domainOptions(data.tp_receita_despesa, "valor"),
    tpComposicao: domainOptions(data.tp_composicao, "valor"),
    veiculos: domainOptions(data.param_tipo_rodado, "partiprod_id"),
    tiposTaxaTda: domainOptions(data.taxa_tp_taxa_tda, "valor"),
    tiposTaxaTde: domainOptions(data.taxa_tp_taxa_tde, "valor"),
    tiposTaxaPedagio: domainOptions(data.taxa_tp_taxa_pedagio, "valor"),
    tpTrecho: domainOptions(data.extra_tp_trecho, "valor"),
    tpTrechoCusto: domainOptions(data.extra_tp_trecho_custo, "valor"),
    custos: domainOptions(data.custo, "custo_id"),
    municipios: municipioOptions(data.municipio),
    regioes: domainOptions(data.regiao, "regiao_id"),
    paramTipoCarga: domainOptions(data.param_tipo_carga, "partipcar_id"),
    tpCarga: domainOptions(data.tp_carga, "tpcarga_id"),
    statusCarga: domainOptions(data.status_carga, "valor"),
    tpProcesso: orderByLabels(
      domainOptions(data.tp_processo, "valor", { keepApiOrder: true }),
      TP_PROCESSO_ORDER,
    ),
    tpOperacao: domainOptions(data.tp_operacao, "valor", {
      keepApiOrder: true,
    }),
  };
}

const MODAL_RODOVIARIO = 1; // b_modal: 1 = Rodoviário
function localityPayload(value) {
  const id = toNumber(value, null);
  return id === null ? null : { value: id };
}

/**
 * Trajeto no formato do §4.8. Roteiro e destino são exclusivos: com
 * itinerary_id o back exige tp_destino ausente e zera os dois campos de
 * destino. tp_origem/tp_destino são derivados de qual lado foi preenchido.
 */
function mapRouteLocationToApi(route) {
  const regiaoOrigem = localityPayload(route.regiao_origem);
  const regiaoDestino = localityPayload(route.regiao_destino);
  const itineraryId = toNumber(route.itinerary_id, null);
  const municipioDestino = localityPayload(route.municipio_destino);
  const temDestino =
    itineraryId === null && (municipioDestino !== null || regiaoDestino !== null);

  return {
    tp_origem: regiaoOrigem ? ROUTE_LOCALITY_REGIAO : ROUTE_LOCALITY_MUNICIPIO,
    municipio_origem: regiaoOrigem
      ? null
      : localityPayload(route.municipio_origem),
    regiao_origem: regiaoOrigem,
    tp_destino: temDestino
      ? regiaoDestino
        ? ROUTE_LOCALITY_REGIAO
        : ROUTE_LOCALITY_MUNICIPIO
      : null,
    municipio_destino: temDestino && !regiaoDestino ? municipioDestino : null,
    regiao_destino: temDestino ? regiaoDestino : null,
    itinerary_id: itineraryId,
  };
}

const NAO = 2; // carga_perigosa / refrigerado: 1 = Sim, 2 = Não

// Data fim vazia precisa ir como null, nunca "": no caminho de update o back
// roda applyVigenciaEndDates com emptyStringMeansOpen: false, então "" não seria
// entendido como vigência aberta.
function dateOrNull(value) {
  const date = String(
    value === null || value === undefined ? "" : value,
  ).trim();
  return date || null;
}

function validityOrderKey(validity) {
  const parts = String(validity.dt_vigencia_ini || "").split("/");
  return parts.length === 3 ? `${parts[2]}${parts[1]}${parts[0]}` : "";
}

function distinctVehicles(validities) {
  const vehicles = [];

  validities.forEach((validity) => {
    validityVehicles(validity).forEach((raw) => {
      const vehicle = toNumber(raw, null);
      if (vehicle !== null && vehicles.indexOf(vehicle) === -1) {
        vehicles.push(vehicle);
      }
    });
  });

  return vehicles;
}

function collectValoresSaidaTransp(route) {
  const byVehicle = new Map();

  (route.vigencia || []).forEach((validity) => {
    validityVehicles(validity).forEach((vehicleId) => {
      const vehicle = toNumber(vehicleId, null);
      if (vehicle !== null && !byVehicle.has(vehicle)) {
        byVehicle.set(vehicle, null);
      }
    });

    if (!validity.saidaVeiculoAtiva) return;

    (validity.valoresSaida || []).forEach((linha) => {
      const vlr =
        linha.vlr === null || linha.vlr === undefined || linha.vlr === ""
          ? null
          : toNumber(linha.vlr, null);

      // Uma linha pode atender vários veículos, mas o contrato continua sendo
      // um transp por veículo: o mesmo vlr_viagem é repetido em cada um.
      valorSaidaLineVehicles(linha).forEach((vehicleId) => {
        const vehicle = toNumber(vehicleId, null);
        if (vehicle === null) return;

        byVehicle.set(vehicle, vlr);
      });
    });
  });

  return Array.from(byVehicle.entries()).map(([vehicle, vlr]) => ({
    tp_rodado: { value: vehicle },
    carroceria: { value: null },
    carga_perigosa: NAO,
    refrigerado: NAO,
    status: 1,
    vlr_viagem: vlr,
  }));
}

function mapTranspToApi(route, tpCalc) {
  if (calcIsFaixaPesoPorVeiculo(tpCalc)) {
    return collectValoresSaidaTransp(route);
  }

  if (!calcUsesVehicle(tpCalc)) {
    return [];
  }

  const validities = route.vigencia || [];

  return distinctVehicles(validities).map((vehicle) => ({
    tp_rodado: { value: vehicle },
    carroceria: { value: null },
    carga_perigosa: NAO,
    refrigerado: NAO,
    status: 1,
    vlr_viagem: null,
  }));
}

/** Carga pronta para o PUT: tipo, tipo carga e status preenchidos. */
function isCargaComplete(carga) {
  return (
    carga != null &&
    carga.param_tipo_carga != null &&
    carga.tp_carga != null &&
    carga.status != null
  );
}

/**
 * Faixas de cálculo da vigência. Nos tipos que exigem faixas a tela edita cada
 * linha; nos outros existe uma linha só e o valor vem do "Valor frete" da
 * vigência, com vlr_ini/vlr_fim zerados — que é o que o §4.8 dispensa neles.
 */
function mapFaixasToApi(faixas, vlrVigencia) {
  const linhas = Array.isArray(faixas) && faixas.length ? faixas : [{}];

  return linhas.map((faixa) => {
    const valor =
      faixa.vlr === null || faixa.vlr === undefined ? vlrVigencia : faixa.vlr;

    return {
      vlr_ini: toNumber(faixa.vlr_ini, 0),
      vlr_fim: toNumber(faixa.vlr_fim, 0),
      vlr: toNumber(valor, 0),
      vlr_exd: toNumber(faixa.vlr_exd, 0),
      fixa: toNumber(faixa.fixa, 0),
      excedente: toNumber(faixa.excedente, 0),
      fator_conv: toNumber(faixa.fator_conv, 1),
      status: toNumber(faixa.status, 1),
    };
  });
}

function mapValidityToApi(validity) {
  const calculo = (validity.calculo || [])[0] || {};
  const vlr =
    validity.vlr === null || validity.vlr === undefined
      ? calculo.vlr
      : validity.vlr;

  return {
    dt_vigencia_ini: dateOrNull(validity.dt_vigencia_ini),
    dt_vigencia_fim: dateOrNull(validity.dt_vigencia_fim),
    status: 1,
    obs_vigencia: "",
    ...mapTaxasToApi(validity),
    // obs_valores nunca persiste: o $fillable da taxa tem `obs` e nada remapeia
    // (ADR-0011 §3). Segue "" — a rule AlphaNumericSpaces só não dispara vazia.
    obs_valores: "",
    calculo: mapFaixasToApi(validity.calculo, vlr),
    custo_extra: mapCustosExtraToApi(validity.custo_extra),
  };
}

export function mapRouteToApi(route, index, numRotaOffset = 0, tpCalc = null) {
  // O back encadeia/cancela vigências abertas assumindo ordem cronológica.
  // Linha em branco (o "Nova vigência" que ninguém preencheu) não vai no PUT:
  // dt_vigencia_ini é required e derrubaria a rota inteira em 422.
  const validities = (route.vigencia || [])
    .filter((validity) => !isValidityBlank(validity))
    .sort((a, b) => validityOrderKey(a).localeCompare(validityOrderKey(b)));

  return {
    num_rota: index + 1 + numRotaOffset,
    descr: route.descricao || null,
    status: toNumber(route.status, 1),
    ...mapRouteLocationToApi(route),
    tp_processo: toNumber(route.tp_processo, null),
    tp_operacao: toNumber(route.tp_operacao, null),
    carga: (route.carga || []).filter(isCargaComplete).map((carga) => ({
      param_tipo_carga: { value: toNumber(carga.param_tipo_carga, null) },
      tp_carga: { value: toNumber(carga.tp_carga, null) },
      status: toNumber(carga.status, 1),
    })),
    // transp é derivado dos veículos das vigências (ADR-0010 §5), ou das linhas
    // de valor de saída no tipo Faixa Peso por Veículo (TMS-7167).
    transp: mapTranspToApi(route, tpCalc),
    vigencia: validities.map((validity) => mapValidityToApi(validity)),
  };
}

// Payload AS-IS do PUT /tabelafrete/store (etapa 3 do rascunho). Toda chave
// precisa estar presente, mesmo nula: chave ausente estoura 500 no legado.
export function mapConfigRoutesToApi({
  form,
  tabfreId,
  identification,
  suppliers,
}) {
  const config = form || {};
  const header = identification || {};
  // cd é NOT NULL e vem do rascunho. Mandar vazio faria o update tentar zerá-lo
  // ("Column 'cd' cannot be null"); omitir a chave deixa o valor atual intacto.
  const codigo = String(header.codigo || "").trim();

  return {
    tabfre_id: Number(tabfreId),
    ...(codigo ? { cd: codigo } : {}),
    descr: header.descricao,
    obs: header.observacao || "",
    tp_calc: config.tp_calc,
    tp_tabela: config.tp_tabela,
    tp_receita_despesa: config.tp_receita_despesa,
    tp_composicao: config.tp_composicao,
    modal: [{ bmodal_id: MODAL_RODOVIARIO, status: 1 }],
    transportador: (suppliers || []).map((supplier) => ({
      pessoa_id: supplier.pessoa_id,
      status: 1,
    })),
    pessoa:
      Number(config.tp_receita_despesa) === FREIGHT_TABLE_TIPO_RECEITA
        ? (config.pessoa || []).map((client) => ({
            pessoa_id: client.pessoa_id,
            status: 1,
          }))
        : [],
    rota: (config.rota || []).map((route, index) =>
      mapRouteToApi(route, index, 0, config.tp_calc),
    ),
  };
}

function domainScalar(raw, relationKey, relationFallbackKey, scalarKey) {
  const rel = relation(raw, relationKey, relationFallbackKey);
  if (rel && rel.valor !== undefined && rel.valor !== null) {
    return toNumber(rel.valor, null);
  }

  return toNumber(raw && raw[scalarKey], null);
}

export function mapHeaderToConfigRoutesForm(raw) {
  return {
    tp_calc: domainScalar(raw, "TpCalculo", "tp_calculo", "tp_calc"),
    tp_tabela: domainScalar(raw, "TpTabela", "tp_tabela", "tp_tabela"),
    tp_receita_despesa: domainScalar(
      raw,
      "TpReceitaDespesa",
      "tp_receita_despesa",
      "tp_receita_despesa",
    ),
    tp_composicao: domainScalar(
      raw,
      "TpComposicao",
      "tp_composicao",
      "tp_composicao",
    ),
  };
}

function sanitizeAlphaNumericSpaces(value) {
  return String(value === null || value === undefined ? "" : value)
    .replace(/[^0-9A-Za-zÀ-ÿ\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function mapNewRoutesToApi({
  tabfreId,
  routes,
  header,
  descricao,
  numRotaOffset = 0,
}) {
  const config = header || {};

  return {
    tabfre_id: Number(tabfreId),
    descr:
      sanitizeAlphaNumericSpaces(descricao) || `Tabela ${Number(tabfreId)}`,
    tp_calc: config.tp_calc,
    tp_tabela: config.tp_tabela,
    tp_receita_despesa: config.tp_receita_despesa,
    tp_composicao: config.tp_composicao,
    rota: (routes || []).map((route, index) =>
      mapRouteToApi(route, index, numRotaOffset, config.tp_calc),
    ),
  };
}
